/* External */
import {
  useState, memo, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import CardCreate from '@dtvgo/card-create-profile';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Utils */
import FocusableButtonProfile from '../../../../components/FocusableButtonProfile';
import { useOnBackPress } from '../../../../utils/keyDownEvents';
import { Routes, getLink } from '../../../../utils/router';
import {
  BR_AGE_RATING_DATA,
  BR_AGE_RATING_LABELS,
  BR_DEFAULT_RATING_STEP,
  BR_GET_LAST_RATING,
  colors,
} from '../../../../utils/constants';
import {
  getKeyByValue,
  getInitials,
  checkUnratedContent,
  featureFlagParentalControl,
} from '../../../../utils/profiles';
import { useCountry, useIsBrazil } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* Other */
import chevronLeft from '../../../../assets/svg/chevron-left.svg';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

/* Components */
import ScrollBarContent from '../../../../components/ScrollBarContent/ScrollBarContent';
import FocusableEditUnratedContentProfile from '../../../../components/FocusableEditUnratedContentProfile/FocusableEditUnratedContentProfile';
import AgeRatingProgressSteps from '../AgeRatingProgressSteps/AgeRatingProgressSteps';

/* Storage */
import { saveProfileAgeRating } from '../../../../storage/profiles/actions';

/* Style */
import './CreateAllowedContent.scss';

function CreateAllowedContent({ realFocusKey, setFocus }) {
  const params = useParams();
  const profileAgeRating = useSelector((state) => state.profile.saveProfileAgeRating);
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const [profiles] = useState(userProfiles);
  const country = useCountry();
  const selectedProfile = profiles?.find((profile) => profile?.id === params.id);
  const ratedContentAllowedProfile = params.id ? selectedProfile.isNotRatedContentAllowed : true;
  const ageRatingProfile = params.id ? selectedProfile.ageRating : BR_DEFAULT_RATING_STEP;
  const getActiveStep = getKeyByValue(BR_AGE_RATING_DATA, ageRatingProfile);
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const hasParentalControl = featureFlagParentalControl(country);

  const [isUnratedContentChecked, setIsUnratedContentChecked] = useState(
    checkUnratedContent(profileAgeRating)
      ? profileAgeRating.isUnratedContentChecked
      : ratedContentAllowedProfile,
  );
  const [activeStep, setActiveStep] = useState(
    profileAgeRating?.ageRating ?? getActiveStep,
  );
  const [ageRatingLabel, setAgeRatingLabel] = useState(
    profileAgeRating?.ageRatingLabel ?? BR_AGE_RATING_LABELS[BR_DEFAULT_RATING_STEP],
  );
  const [ageRatingData, setAgeRatingData] = useState({});
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const UnratedContentProfileFocusKey = `${realFocusKey}-focus-unrated-content-profile`;
  const newProfileName = `Perfil ${(profiles?.length || 0) + 1}`;
  const cardColor = params.id
    ? colors[location.state?.indexColor]
    : colors[(profiles?.length || 0) + 1];

  useEffect(() => {
    setAgeRatingData({
      ageRatingLabel,
      ageRating: activeStep,
      isUnratedContentChecked,
    });
  }, [ageRatingLabel, activeStep, isUnratedContentChecked, params]);

  const profileCommonTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PARENTAL_CONTROL_DATA,
        subCategory: GTM_SUBCATEGORIES.ALLOW_CONTENT,
        eventName,
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    if (tagsMountRef.current && hasParentalControl) {
      profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, hasParentalControl]);

  const handleClick = useCallback(({ id, indexColor }) => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PARENTAL_CONTROL_ALLOW_CONTENT.CONTINUE });
    dispatch(saveProfileAgeRating({ ...ageRatingData }));
    if (id) {
      history.push(getLink(Routes.Private.Profiles.Edit, { id }), { indexColor });
    } else {
      history.push(getLink(Routes.Private.Profiles.Create.Resume, { name: localStorage.getItem('username') }), { indexColor });
    }
  }, [dispatch, ageRatingData, history, profileCommonTagEvent]);

  const handleBackToEditUser = useCallback(({ id, indexColor }) => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PARENTAL_CONTROL_ALLOW_CONTENT.BACK });
    if (id) {
      history.push(getLink(Routes.Private.Profiles.Edit, { id }), { indexColor });
    } else {
      history.push(getLink(Routes.Private.Profiles.Create.Resume, { name: localStorage.getItem('username') }), { indexColor });
    }
  }, [history, profileCommonTagEvent]);

  function inputValue() {
    return params.id ? selectedProfile.name : newProfileName;
  }

  const handleBackPress = useCallback(() => {
    const id = selectedProfile?.id;
    const indexColor = location.state?.indexColor;
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PARENTAL_CONTROL_ALLOW_CONTENT.BACK });

    if (id && indexColor) {
      return history.push(getLink(Routes.Private.Profiles.Edit, { id }), { indexColor });
    }
    return history.goBack();
  }, [history, location, selectedProfile, profileCommonTagEvent]);

  const handleEditUnratedContent = () => setIsUnratedContentChecked(!isUnratedContentChecked);

  useOnBackPress({ listener: handleBackPress });

  // This regex returns the first 2 letters of the selected profile name
  const userNameSplit = getInitials(inputValue());
  const isBrazil = useIsBrazil();

  return (
    <div className="dtv-html5-c-profile-allow-content">
      <div className="dtv-html5-c-profile-allow-content--div">
        <div className="dtv-html5-c-title__back">
          {profiles?.length > 0
            ? (
              <img
                src={getJsonImage('chevron-left') || chevronLeft}
                alt="Chevron Left"
                className="dtv-html5-c-title__back--chevronLeft"
              />
            )
            : ''}
          <h1>
            <FormattedMessage id="profiles.parentalControl.content.allow" />
          </h1>
        </div>
        <div className="dtv-html5-c-profile-allow-content--row">
          <div className="dtv-html5-c-profile-allow-content--row-col_profile">
            <div className="dtv-html5-c-profile-allow-content--row-col_profile-div">
              <CardCreate
                color={cardColor}
                isInputNotEmpty={userNameSplit}
              />
            </div>
          </div>
          <div className="dtv-html5-c-profile-allow-content--row-col_data">
            <ScrollBarContent>
              <div className="dtv-html5-c-profile-allow-content--row-section">

                <div className="dtv-html5-c-profile-allow-content--row-col_data_title">
                  <h2 className="dtv-html5-c-profile-allow-content--row-col_data_title_h2">
                    <FormattedMessage id="profiles.parentalControl.limit.classification" />
                  </h2>
                </div>

                <div className="dtv-html5-c-profile-allow-content--row-block">
                  <p>
                    <FormattedMessage id="profiles.parentalControl.select.age.limit" />
                    {isBrazil && (<span><FormattedMessage id="profiles.parentalControl.self.classified.content" /></span>)}
                  </p>
                </div>

                <div className="dtv-html5-c-profile-allow-content--row-age-rating-container">
                  <AgeRatingProgressSteps
                    isBrazil
                    activeStep={activeStep || BR_GET_LAST_RATING}
                    ageRatingLabel={ageRatingLabel}
                    setAgeRatingLabel={setAgeRatingLabel}
                    setActiveStep={setActiveStep}
                    ageRatingData={BR_AGE_RATING_DATA}
                    ageRatingLabels={BR_AGE_RATING_LABELS}
                    isAgeRatingForWeb={false}
                    realFocusKey={realFocusKey}
                    setFocus={setFocus}
                  />
                </div>

                <div className="dtv-html5-c-profile-allow-content--row-col_data_title">
                  <h2 className="dtv-html5-c-profile-allow-content--row-col_data_title_h2">
                    <FormattedMessage id="profiles.parentalControl.other.restrictions" />
                  </h2>
                </div>
                {selectedProfile?.id
                  ? (
                    <FocusableEditUnratedContentProfile
                      id={selectedProfile?.id}
                      focusKey={UnratedContentProfileFocusKey}
                      indexColor={location.state?.indexColor}
                      onEnterPress={handleEditUnratedContent}
                      checked={isUnratedContentChecked}
                      focused
                    />
                  ) : (
                    <FocusableEditUnratedContentProfile
                      onEnterPress={handleEditUnratedContent}
                      checked={isUnratedContentChecked}
                      focused
                    />
                  )}
                <div className="dtv-html5-c-profile-allow-content--row-extract">
                  <p><FormattedMessage id="profiles.parentalControl.content.info" /></p>
                </div>

              </div>
            </ScrollBarContent>
            <div className="dtv-html5-c-profile-allow-content--buttons">
              <FocusableButtonProfile
                id={selectedProfile?.id}
                indexColor={location.state.indexColor}
                className="dtv-html5-c-profile-allow-content--buttons-pattern 'dtv-btn-blue dtv-html5-c-create-profile__button"
                secondGray
                bordered
                onEnterPress={handleClick}
              >
                <FormattedMessage id="common.continue" />
              </FocusableButtonProfile>
              <FocusableButtonProfile
                id={selectedProfile?.id}
                indexColor={location.state.indexColor}
                secondGray
                bordered
                onEnterPress={handleBackToEditUser}
              >
                <FormattedMessage id="common.not.now" />
              </FocusableButtonProfile>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

CreateAllowedContent.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(CreateAllowedContent)));
